import axios from "axios";
import Spinner from "components/Spinner";
import { setNotification } from "features/Notification/notificationSlice";
import DeleteModalButton from "components/DeleteModalBtn/DeleteModalButton";
import { useCallback, useEffect, useMemo, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { useDispatch, useSelector } from "react-redux";
import authHeader from "services/auth-header";
import API from "services/axios";
function LeadGeneralDetails({ leadDetails, refreshFunc }) {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user?.userInfo);

  const [locations, setLocations] = useState(null);
  const [salesPersons, setSalesPersons] = useState(null);
  const [salesManagers, setSalesManagers] = useState(null);
  const [paymentTerms, setPaymentTerms] = useState(null);
  const [discountLevels, setDiscountLevels] = useState(null);
  const [taxAccounts, setTaxAccounts] = useState(null);

  const [showTaxModal, setShowTaxModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({});
  const [examptionFile, setExamptionFile] = useState(null);

  const fetchLocations = useCallback(async () => {
    try {
      const { data } = await API.get("/locations", { headers: authHeader() });
      setLocations(data);
    } catch (err) {
      dispatch(setNotification({ message: err.message, type: "error" }));
    }
  }, [dispatch]);

  const fetchSalesPersons = useCallback(async () => {
    try {
      const { data } = await API.get("/salespersons", {
        headers: authHeader(),
      });
      setSalesPersons(data);
    } catch (err) {
      dispatch(setNotification({ message: err.message, type: "error" }));
    }
  }, [dispatch]);

  const fetchSalesManagers = useCallback(async () => {
    try {
      const { data } = await API.get("/salesmanagers", {
        headers: authHeader(),
      });
      setSalesManagers(data);
    } catch (err) {
      dispatch(setNotification({ message: err.message, type: "error" }));
    }
  }, [dispatch]);

  const fetchPaymentTerms = useCallback(async () => {
    try {
      const { data } = await API.get("/paymentterms", {
        headers: authHeader(),
      });
      setPaymentTerms(data);
    } catch (err) {
      dispatch(setNotification({ message: err.message, type: "error" }));
    }
  }, [dispatch]);

  const fetchDiscountLevels = useCallback(async () => {
    try {
      const { data } = await API.get("/discountlevels", {
        headers: authHeader(),
      });
      setDiscountLevels(data);
    } catch (err) {
      dispatch(setNotification({ message: err.message, type: "error" }));
    }
  }, [dispatch]);

  const fetchTaxAccounts = useCallback(async () => {
    try {
      const { data } = await API.get("/taxrate", { headers: authHeader() });
      setTaxAccounts(data);
    } catch (err) {
      dispatch(setNotification({ message: err.message, type: "error" }));
    }
  }, [dispatch]);

  useEffect(() => {
    let timeout = setTimeout(() => {
      fetchLocations();
      fetchSalesPersons();
      fetchSalesManagers();
      fetchPaymentTerms();
      fetchDiscountLevels();
      fetchTaxAccounts();
    }, 50);

    return () => clearTimeout(timeout);
  }, [
    fetchLocations,
    fetchSalesPersons,
    fetchSalesManagers,
    fetchPaymentTerms,
    fetchDiscountLevels,
    fetchTaxAccounts,
  ]);

  useEffect(() => {
    if (leadDetails) {
      setFormData({
        locationId:
          leadDetails?.locationId ?? leadDetails?.location?.id ?? null,
        salesPersonId: leadDetails?.salesPersonId ?? null,
        salesManager: leadDetails?.salesManager ?? null,
        paymentTermsId: leadDetails?.paymentTermsId ?? null,
        poRequired: Boolean(leadDetails?.poRequired),
        creditLimit: leadDetails?.creditLimit ?? "",
        creditHold: Boolean(leadDetails?.creditHold),
        discountId: leadDetails?.discountId ?? null,
        isCommission: Boolean(leadDetails?.isCommission),
        commissionType: leadDetails?.commissionType ?? "",
        commissionValue: leadDetails?.commissionValue ?? "",
        taxAccountId: leadDetails?.taxAccountId ?? null,
        exemptionNumber: leadDetails?.exemptionNumber ?? "",
        isTaxExamption: Boolean(leadDetails?.isTaxExamption),
        taxNumber: leadDetails?.taxNumber ?? "",
        examptionFile: leadDetails?.examptionFile ?? null,
      });
      setExamptionFile(null);
    }
  }, [leadDetails]);

  const locationName = useCallback(
    (id) => locations?.find((item) => item.id === id)?.locationName ?? "-",
    [locations],
  );

  const salesPersonName = useCallback(
    (id) => salesPersons?.find((item) => item.id === id)?.name ?? "-",
    [salesPersons],
  );

  const salesManagerName = useCallback(
    (id) => {
      const found = salesManagers?.find((item) => item.id === id);
      return found ? `${found.firstName} ${found.lastName}` : "-";
    },
    [salesManagers],
  );

  const paymentTermName = useCallback(
    (id) => paymentTerms?.find((item) => item.id === id)?.termName ?? "-",
    [paymentTerms],
  );

  const discountName = useCallback(
    (id) => discountLevels?.find((item) => item.id === id)?.discountName ?? "-",
    [discountLevels],
  );

  const taxAccountName = useCallback(
    (id) => {
      const account = taxAccounts?.find((item) => item.id === id);
      if (account) {
        return `${account.taxAccountNumber} : ${account.taxAccountName}`;
      }
      return "-";
    },
    [taxAccounts],
  );

  const handleExamptionFile = (event) => {
    const file = event.target.files?.[0] || null;
    setExamptionFile(file);
  };

  const uploadExamptionFile = async (file) => {
    if (!file || !leadDetails?.leadId) return true;

    try {
      const certificateForm = new FormData();
      certificateForm.append("file", file);
      certificateForm.append(
        "folder",
        `kms/lead/${leadDetails.leadId}/certificates`,
      );
      certificateForm.append("leadId", leadDetails.leadId);
      if (user?.id) {
        certificateForm.append("uploadedBy", user.id);
      }
      if (user?.companyId) {
        certificateForm.append("companyId", user.companyId);
      }
      certificateForm.append("documentType", "examptionFile");

      await API.post("/lead-file/upload", certificateForm, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      dispatch(
        setNotification({
          message: "Certificate uploaded successfully",
          type: "success",
        }),
      );
      return true;
    } catch (err) {
      dispatch(
        setNotification({
          message: err.message || "Failed to upload certificate",
          type: "error",
        }),
      );
      return false;
    }
  };

  const downloadFile = async (fileKey) => {
    if (!fileKey) return;
    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_API_URI}/aws/images`,
        { key: fileKey },
      );
      const url = data?.[0];
      if (!url) return;

      const link = document.createElement("a");
      link.href = url;
      // Opens in a new tab (user, 2026-11-02) instead of downloading.
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      dispatch(setNotification({ message: err.message, type: "error" }));
    }
  };

  const deleteFile = async ({ id, type }) => {
    if (!id || !leadDetails?.leadId) return;
    try {
      setIsLoading(true);
      await API.post(
        "/lead-file/delete-file",
        { id: leadDetails.leadId, file: id, documentType: type },
        { headers: authHeader() },
      );
      dispatch(
        setNotification({
          message: "Deleted successfully",
          type: "success",
        }),
      );
      setFormData((prev) => ({ ...prev, examptionFile: null }));
      if (typeof refreshFunc === "function") {
        refreshFunc();
      }
    } catch (err) {
      dispatch(setNotification({ message: err.message, type: "error" }));
    } finally {
      setIsLoading(false);
      setExamptionFile(null);
    }
  };

  const updateLeadGeneral = async (e) => {
    e.preventDefault();
    if (!leadDetails?.leadId) return;

    const payload = {
      ...formData,
      id: leadDetails?.id,
      locationId: formData.locationId || null,
      salesPersonId: formData.salesPersonId || null,
      salesManager: formData.salesManager || null,
      paymentTermsId: formData.paymentTermsId || null,
      discountId: formData.discountId || null,
      taxAccountId: formData.taxAccountId || null,
      exemptionNumber: formData.exemptionNumber || null,
      taxNumber: formData.taxNumber || null,
      poRequired: Boolean(formData.poRequired),
      creditHold: Boolean(formData.creditHold),
      isCommission: Boolean(formData.isCommission),
      isTaxExamption: Boolean(formData.isTaxExamption),
      creditLimit:
        formData.creditLimit === "" || formData.creditLimit == null
          ? null
          : Number(formData.creditLimit),
      commissionValue:
        formData.commissionValue === "" || formData.commissionValue == null
          ? null
          : Number(formData.commissionValue),
      leadId: leadDetails?.leadId,
    };

    setIsLoading(true);
    try {
      const updatedData = JSON.stringify(payload);
      const { data } = await API.patch(
        "/leads",
        { updatedData },
        { headers: authHeader() },
      );

      if (data?.success) {
        const uploaded = await uploadExamptionFile(examptionFile);
        if (typeof refreshFunc === "function") {
          refreshFunc();
        }
        if (uploaded) {
          setShowTaxModal(false);
          setExamptionFile(null);
        }
      } else {
        dispatch(
          setNotification({
            message: data?.message || "Unable to update lead",
            type: "error",
          }),
        );
      }
    } catch (err) {
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        }),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const uploadFiles = async (file, type) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("folder", `kms/leads/${leadDetails.id}/certificates`);
    formData.append("documentType", type);
    formData.append("id", leadDetails?.id);

    if (file == null) {
      dispatch(
        setNotification({
          message: "No file selected for upload.",
          type: "error",
        }),
      );
      return;
    }

    await API.post("/leads/upload", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  };

  const updateTaxDetails = async (e) => {
    e.preventDefault();
    let updateData = {
      ...formData,
      locationId: formData.locationId === "null" ? null : formData.locationId,
      salesPersonId:
        formData.salesPersonId === "null" ? null : formData.salesPersonId,
      salesManager:
        formData.salesManager === "null" ? null : formData.salesManager,
      taxAccountId:
        formData.taxAccountId === "null" ? null : formData.taxAccountId,
      creditLimit:
        formData.creditLimit === "" || formData.creditLimit == null
          ? null
          : Number(formData.creditLimit),
      commissionValue:
        formData.commissionValue === "" || formData.commissionValue == null
          ? null
          : Number(formData.commissionValue),
    };
    const updatedData = JSON.stringify({
      ...updateData,
      id: leadDetails.id,
    });

    setIsLoading(true);
    try {
      const { data } = await API.patch(
        "/leads",
        { updatedData },
        { headers: authHeader() },
      );

      if (data.success) {
        if (examptionFile) await uploadFiles(examptionFile, "examptionFile");
        refreshFunc(leadDetails.id);
        setIsLoading(false);
        setShowTaxModal(false);
        return;
      }
      console.log(data);
      setIsLoading(false);
    } catch (err) {
      setIsLoading(false);
      console.log(err.message);
    }
  };

  const ready = useMemo(() => {
    return (
      locations &&
      salesPersons &&
      salesManagers &&
      paymentTerms &&
      discountLevels &&
      taxAccounts
    );
  }, [
    locations,
    salesPersons,
    salesManagers,
    paymentTerms,
    discountLevels,
    taxAccounts,
  ]);

  if (!ready) {
    return <Spinner />;
  }

  return (
    <div className="flex-grow-1">
      <div className="d-flex justify-content-between align-items-center">
        <div className="d-flex flex-grow-1">
          <h4 className="border-bottom border-secondary">
            GENERAL SALES DETAIL
          </h4>
        </div>
        <button
          className="btn d-flex justify-content-center align-items-center btn-sm btn-success"
          style={{ width: 28, height: 28 }}
          onClick={() => setShowTaxModal(true)}
        >
          <i className="mdi fs-5 mdi-pencil"></i>
        </button>
      </div>
      <div className="row">
        <div className="col-5">Brand</div>
        <div className="col-1">:</div>
        <div className="col-6">{locationName(leadDetails?.locationId)}</div>
      </div>
      <div className="row">
        <div className="col-5">Inside Sales Person</div>
        <div className="col-1">:</div>
        <div className="col-6">
          {salesManagerName(leadDetails?.salesManager)}
        </div>
      </div>
      <div className="row">
        <div className="col-5">Outside Sales Person</div>
        <div className="col-1">:</div>
        <div className="col-6">
          {salesPersonName(leadDetails?.salesPersonId)}
        </div>
      </div>
      <div className="row">
        <div className="col-5">Payment Term</div>
        <div className="col-1">:</div>
        <div className="col-6">
          {paymentTermName(leadDetails?.paymentTermsId)}
        </div>
      </div>
      <div className="row">
        <div className="col-5">Po Required</div>
        <div className="col-1">:</div>
        <div className="col-6">{leadDetails?.poRequired ? "Yes" : "No"}</div>
      </div>
      <div className="row">
        <div className="col-5">Credit Limit</div>
        <div className="col-1">:</div>
        <div className="col-6">
          {leadDetails?.creditLimit ? leadDetails.creditLimit : "-"}
        </div>
      </div>
      <div className="row">
        <div className="col-5">Credit Hold</div>
        <div className="col-1">:</div>
        <div className="col-6">{leadDetails?.creditHold ? "Yes" : "No"}</div>
      </div>
      <div className="row">
        <div className="col-5">Discount</div>
        <div className="col-1">:</div>
        <div className="col-6">{discountName(leadDetails?.discountId)}</div>
      </div>
      <div className="row">
        <div className="col-5">Commission</div>
        <div className="col-1">:</div>
        <div className="col-6">{leadDetails?.isCommission ? "Yes" : "No"}</div>
      </div>
      <div className="row">
        <div className="col-5">Commission Type</div>
        <div className="col-1">:</div>
        <div className="col-6">{leadDetails?.commissionType || "-"}</div>
      </div>
      <div className="row">
        <div className="col-5">Commission Value</div>
        <div className="col-1">:</div>
        <div className="col-6">{leadDetails?.commissionValue || "-"}</div>
      </div>

      <hr className="me-2" />
      <div className="d-flex">
        <h4 className="border-bottom border-secondary">TAX DETAILS</h4>
      </div>
      <div className="row">
        <div className="col-5">
          <b>Tax Account</b>
        </div>
        <div className="col-1">:</div>
        <div className="col-6">{taxAccountName(leadDetails?.taxAccountId)}</div>
      </div>
      <div className="row">
        <div className="col-5">Tax Exempted</div>
        <div className="col-1">:</div>
        <div className="col-6">
          {leadDetails?.isTaxExamption ? "Yes" : "No"}
        </div>
      </div>
      <div className="row">
        <div className="col-5">Tax Number</div>
        <div className="col-1">:</div>
        <div className="col-6">{leadDetails?.taxNumber || "-"}</div>
      </div>
      <div className="row">
        <div className="col-5">Exemption Number</div>
        <div className="col-1">:</div>
        <div className="col-6">{leadDetails?.exemptionNumber || "-"}</div>
      </div>
      <div className="row">
        <div className="col-5">W-9</div>
        <div className="col-1">:</div>
        <div className="col-6">-</div>
      </div>
      <div className="row d-flex align-items-center">
        <div className="col-5">Tax Exemption Certificate</div>
        <div className="col-1">:</div>
        <div className="col-6">
          {leadDetails?.examptionFile ? "Added" : "Not Added"}
        </div>
      </div>

      <Modal
        show={showTaxModal}
        onHide={() => setShowTaxModal(false)}
        size="xl"
        contentClassName="bg-transparent"
      >
        <Modal.Body className="bg-white rounded-3 px-4">
          <div>
            <h3>Update form</h3>
            <hr className="mt-0" />
            <form onSubmit={updateTaxDetails}>
              <div className="row">
                <div className="col-md-6">
                  <div>
                    <h4 className="mb-0">General Sales Details</h4>
                  </div>
                  <hr className="mt-0" />
                  <div className="row mb-1">
                    <label className="col-3">Brand</label>
                    <div className="col-9">
                      <select
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            locationId:
                              e.target.value === "null"
                                ? null
                                : Number(e.target.value),
                          })
                        }
                        value={formData?.locationId ?? "null"}
                        className="form-select"
                      >
                        <option value="null">None</option>
                        {locations?.map((item) => (
                          <option key={item?.id} value={item?.id}>
                            {item?.locationName}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="row mb-1">
                    <label className="col-3">Inside Sales Person</label>
                    <div className="col-9">
                      <select
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            salesManager:
                              e.target.value === "null"
                                ? null
                                : Number(e.target.value),
                          })
                        }
                        value={formData?.salesManager ?? "null"}
                        className="form-select"
                      >
                        <option value="null">None</option>
                        {salesManagers?.map((item) => (
                          <option key={item?.id} value={item?.id}>
                            {item?.firstName} {item?.lastName}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="row mb-1">
                    <label className="col-3">Outside Sales Person</label>
                    <div className="col-9">
                      <select
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            salesPersonId:
                              e.target.value === "null"
                                ? null
                                : Number(e.target.value),
                          })
                        }
                        value={formData?.salesPersonId ?? "null"}
                        className="form-select"
                      >
                        <option value="null">None</option>
                        {salesPersons?.map((item) => (
                          <option key={item?.id} value={item?.id}>
                            {item?.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="mt-3">
                    <h4 className="mb-0">Tax Details</h4>
                  </div>
                  <hr className="mt-0" />
                  <div className="row mb-1">
                    <label className="col-3">Tax Account</label>
                    <div className="col-9">
                      <select
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            taxAccountId:
                              e.target.value === "null"
                                ? null
                                : Number(e.target.value),
                          })
                        }
                        value={formData?.taxAccountId ?? "null"}
                        className="form-select"
                      >
                        <option value="null">None</option>
                        {taxAccounts?.map((item) => (
                          <option key={item?.id} value={item?.id}>
                            {item?.taxAccountNumber} : {item?.taxAccountName}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="row mb-1">
                    <label className="col-3">Tax Exempted</label>
                    <div className="col-9">
                      <input
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            isTaxExamption: e.target.checked,
                          })
                        }
                        checked={formData?.isTaxExamption || false}
                        type="checkbox"
                        id="leadTaxExempt"
                        data-switch="none"
                      />
                      <label
                        htmlFor="leadTaxExempt"
                        data-on-label="Yes"
                        data-off-label="No"
                      ></label>
                    </div>
                  </div>
                  <div className="row mb-1">
                    <label className="col-3">Tax Number</label>
                    <div className="col-9">
                      <input
                        type="text"
                        value={formData?.taxNumber ?? ""}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            taxNumber: e.target.value,
                          })
                        }
                        className="form-control"
                        placeholder="Enter tax number"
                      />
                    </div>
                  </div>

                  <div className="row mb-1">
                    <label className="col-3">Tax Exemption Certificate</label>
                    <div className="col-9 d-flex align-items-center">
                      <div className="flex-grow-1">
                        {formData?.examptionFile ? (
                          <div className="form-control">
                            {
                              formData?.examptionFile?.split("/")[
                                formData?.examptionFile?.split("/").length - 1
                              ]
                            }
                          </div>
                        ) : (
                          <input
                            type="file"
                            onChange={handleExamptionFile}
                            className="form-control"
                          />
                        )}
                      </div>
                      <div>
                        <button
                          disabled={!formData?.examptionFile}
                          onClick={() => downloadFile(formData?.examptionFile)}
                          type="button"
                          className={`btn btn-sm btn-${
                            formData?.examptionFile ? "success" : "secondary"
                          } rounded py-0 px-1 ms-1`}
                        >
                          <i className="mdi mdi-arrow-down-bold fs-3"></i>
                        </button>
                      </div>
                      {user?.roles === "admin" && (
                        <div>
                          <DeleteModalButton
                            deleteFunc={deleteFile}
                            deleteParams={{
                              id: formData?.examptionFile,
                              type: "examptionFile",
                            }}
                            btnClass={`btn btn-${
                              formData?.examptionFile ? "danger" : "secondary"
                            } rounded ms-1`}
                            disableConditions={!formData?.examptionFile}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div>
                    <h4 className="mb-0">Payment</h4>
                  </div>
                  <hr className="mt-0" />
                  <div className="row mb-1">
                    <label className="col-4">Po Required</label>
                    <div className="col-8">
                      <input
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            poRequired: e.target.checked,
                          })
                        }
                        checked={formData?.poRequired || false}
                        type="checkbox"
                        id="leadPoRequired"
                        data-switch="none"
                      />
                      <label
                        htmlFor="leadPoRequired"
                        data-on-label="Yes"
                        data-off-label="No"
                      ></label>
                    </div>
                  </div>
                  <div className="row mb-1">
                    <label className="col-4">Payment Terms</label>
                    <div className="col-8">
                      <select
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            paymentTermsId:
                              e.target.value === "null"
                                ? null
                                : Number(e.target.value),
                          })
                        }
                        value={formData?.paymentTermsId ?? "null"}
                        className="form-select"
                      >
                        <option value="null">None</option>
                        {paymentTerms?.map((item) => (
                          <option key={item?.id} value={item?.id}>
                            {item?.termName}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="row mb-1">
                    <label className="col-4 my-auto">Credit Limit</label>
                    <div className="col-8">
                      <input
                        type="number"
                        min={0}
                        value={formData?.creditLimit ?? ""}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            creditLimit: e.target.value,
                          })
                        }
                        className="form-control"
                        placeholder="Enter credit limit"
                      />
                    </div>
                  </div>
                  <div className="row mb-1">
                    <label className="col-4">Credit Hold</label>
                    <div className="col-8">
                      <input
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            creditHold: e.target.checked,
                          })
                        }
                        checked={formData?.creditHold || false}
                        type="checkbox"
                        id="leadCreditHold"
                        data-switch="none"
                      />
                      <label
                        htmlFor="leadCreditHold"
                        data-on-label="Yes"
                        data-off-label="No"
                      ></label>
                    </div>
                  </div>
                  <div className="row mb-1">
                    <label className="col-4 my-auto">Discount</label>
                    <div className="col-8">
                      <select
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            discountId:
                              e.target.value === "null"
                                ? null
                                : Number(e.target.value),
                          })
                        }
                        value={
                          formData?.discountId === null
                            ? "null"
                            : (formData?.discountId ?? "null")
                        }
                        className="form-select"
                      >
                        <option value="null">None</option>
                        {discountLevels?.map((item) => (
                          <option key={item?.id} value={item?.id}>
                            {item?.discountName}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <hr />
                  <div>
                    <h4 className="mb-0">Commission</h4>
                  </div>
                  <hr className="mt-0" />
                  <div className="row mb-1">
                    <label className="col-4">Commission</label>
                    <div className="col-8">
                      <input
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            isCommission: e.target.checked,
                          })
                        }
                        checked={formData?.isCommission || false}
                        type="checkbox"
                        id="leadCommission"
                        data-switch="none"
                      />
                      <label
                        htmlFor="leadCommission"
                        data-on-label="Yes"
                        data-off-label="No"
                      ></label>
                    </div>
                  </div>
                  <div className="row mb-1">
                    <label className="col-4 my-auto">Commission Type</label>
                    <div className="col-8">
                      <input
                        type="text"
                        disabled={!formData?.isCommission}
                        value={formData?.commissionType ?? ""}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            commissionType: e.target.value,
                          })
                        }
                        className="form-control"
                        placeholder="Enter commission type"
                      />
                    </div>
                  </div>
                  <div className="row mb-1">
                    <label className="col-4 my-auto">Commission Value</label>
                    <div className="col-8">
                      <input
                        type="number"
                        min={0}
                        step="any"
                        disabled={!formData?.isCommission}
                        value={formData?.commissionValue ?? ""}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            commissionValue: e.target.value,
                          })
                        }
                        className="form-control"
                        placeholder="Enter commission value"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {isLoading && <Spinner />}

              <div className="flex-grow-1 mt-3 d-flex justify-content-end align-items-end">
                <div>
                  <button
                    onClick={() => setShowTaxModal(false)}
                    type="button"
                    className="btn btn-secondary me-2 px-1"
                  >
                    CLOSE
                  </button>

                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isLoading}
                  >
                    UPDATE
                  </button>
                </div>
              </div>
            </form>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default LeadGeneralDetails;
